import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import api from "../../utils/axios";
import toast from "react-hot-toast";
import {
  FaTrash,
  FaUpload,
  FaArrowLeft,
  FaPlus,
  FaFileUpload,
  FaTimes,
} from "react-icons/fa";

const GAS_PDF_URL =
  "https://script.google.com/macros/s/AKfycbzR9X4SN8qlLGVPYoZRx-_16siw8h9wr8x9CTT78N47GCR9OX8FoYk1SKq8Xe7Zc_qX/exec";

export default function DealerRewards() {
  const { dealerId } = useParams();
  const navigate = useNavigate();

  const [dealer, setDealer] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [hasPdf, setHasPdf] = useState(false);

  const [fromKg, setFromKg] = useState("");
  const [toKg, setToKg] = useState("");
  const [rewardsInput, setRewardsInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const formatRewards = (value) =>
    String(value || "")
      .toUpperCase()
      .replace(/\s*\/\s*/g, " / ")
      .replace(/\s{2,}/g, " ")
      .trim();

  const normalizeGst = (v) => String(v || "").trim().toUpperCase();

  // Fetch PDF URL from Google Sheet (GAS) by GST and pick latest
  const fetchPdfFromSheet = async (gst) => {
    const GST = normalizeGst(gst);
    if (!GST) return null;

    const res = await fetch(`${GAS_PDF_URL}?t=${Date.now()}`);
    const data = await res.json();

    if (!Array.isArray(data)) return null;

    const matches = data
      .map((r) => ({
        gst: normalizeGst(r.GST),
        pdf: String(r.PDF || "").trim(),
        ts: String(r.TIMESTAMP || "").trim(),
      }))
      .filter((r) => r.gst === GST && r.pdf);

    if (!matches.length) return null;

    // Your timestamp is like "06/02/2026 04:35:33" (DD/MM/YYYY HH:mm:ss)
    // We'll convert to Date for correct sorting.
    const toDate = (ts) => {
      // "DD/MM/YYYY HH:mm:ss"
      const m = String(ts).match(
        /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/,
      );
      if (!m) return new Date(0);
      const [, dd, mm, yyyy, HH, MI, SS] = m;
      return new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(HH),
        Number(MI),
        Number(SS),
      );
    };

    matches.sort((a, b) => toDate(b.ts) - toDate(a.ts));
    return matches[0]?.pdf || null;
  };

  const getRewardSuggestions = () => {
    const all = rows.flatMap((r) =>
      String(r.rewards || "")
        .split(" / ")
        .map((x) => x.trim())
        .filter(Boolean),
    );
    return [...new Set(all)];
  };

  const suggestions = useMemo(() => {
    const lastPart = rewardsInput.split("/").pop()?.trim() || "";
    return getRewardSuggestions().filter(
      (item) =>
        item.toLowerCase().includes(lastPart.toLowerCase()) &&
        !rewardsInput.toUpperCase().includes(item),
    );
  }, [rewardsInput, rows]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dealerRes, rewardRes] = await Promise.all([
          api.get(`/admin/dealer/${dealerId}`),
          api.get(`/admin/dealers/${dealerId}/reward`),
        ]);

        // -------- Dealer + PDF (FIX) ----------
        if (dealerRes.data?.success) {
          const d = dealerRes.data.dealer || null;
          setDealer(d);

          const gst =
            d?.dealerGst ||
            d?.gst ||
            d?.GST ||
            d?.dealer?.gst ||
            d?.dealer?.GST ||
            "";

          // Prefer backend url if present
          let pdfUrl = d?.pdf?.url || "";

          // If not present, load from Sheet
          if (!pdfUrl && gst) {
            try {
              pdfUrl = await fetchPdfFromSheet(gst);
            } catch (e) {
              console.error("GAS pdf fetch failed:", e);
            }
          }

          if (pdfUrl) {
            setDealer((prev) => ({ ...(prev || {}), pdf: { url: pdfUrl } }));
            setHasPdf(true);
          } else {
            setHasPdf(false);
          }
        } else {
          setDealer(null);
          setHasPdf(false);
        }

        // -------- Rewards ----------
        if (rewardRes.data?.success && Array.isArray(rewardRes.data.rewards)) {
          setRows(
            rewardRes.data.rewards.map((r) => ({
              range: `${r.fromKg} - ${r.toKg} KG`,
              rewards: Array.isArray(r.rewards) ? r.rewards.join(" / ") : "",
              fromKg: r.fromKg,
              toKg: r.toKg,
              new: false,
            })),
          );
        } else {
          setRows([]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        toast.error("Failed to load data");
      }
    };

    fetchData();
  }, [dealerId]);

  const addRow = () => {
    if (!fromKg || !toKg || !rewardsInput.trim()) {
      toast.error("Fill all fields");
      return;
    }
    if (Number(fromKg) >= Number(toKg)) {
      toast.error("From KG must be less than To KG");
      return;
    }

    setRows((prev) => [
      ...prev,
      {
        range: `${fromKg} - ${toKg} KG`,
        rewards: formatRewards(rewardsInput),
        fromKg: Number(fromKg),
        toKg: Number(toKg),
        new: true,
      },
    ]);

    setFromKg("");
    setToKg("");
    setRewardsInput("");
    setShowSuggestions(false);

    if (window.innerWidth < 768) setShowAddModal(false);
    toast.success("Row added");
  };

  const removeRow = async (index) => {
    const row = rows[index];

    if (row.new) {
      setRows((prev) => prev.filter((_, i) => i !== index));
      toast.success("Row removed", { icon: "🗑️" });
      return;
    }

    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const saveRewards = async () => {
    const newRows = rows.filter((r) => r.new);
    if (!newRows.length) return toast.error("No new rewards to save");

    try {
      setSaving(true);
      const payload = newRows.map((r) => ({
        fromKg: r.fromKg,
        toKg: r.toKg,
        rewards: String(r.rewards || "")
          .split(" / ")
          .map((x) => x.trim())
          .filter(Boolean),
      }));

      const res = await api.post(`/admin/dealers/${dealerId}/reward`, {
        rewards: payload,
      });

      if (res.data?.success) {
        toast.success("Rewards saved!");
        setRows(
          res.data.rewards.map((r) => ({
            range: `${r.fromKg} - ${r.toKg} KG`,
            rewards: Array.isArray(r.rewards) ? r.rewards.join(" / ") : "",
            fromKg: r.fromKg,
            toKg: r.toKg,
            new: false,
          })),
        );
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error("Failed to save rewards");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const uploadPdf = async () => {
    if (!pdfFile) return toast.error("Select PDF first");

    // GST from dealer object
    const dealerGst =
      dealer?.dealerGst ||
      dealer?.gst ||
      dealer?.GST ||
      dealer?.dealer?.gst ||
      dealer?.dealer?.GST ||
      "";

    if (!dealerGst) return toast.error("Dealer GST not found in login");

    const formData = new FormData();
    formData.append("pdf", pdfFile);
    formData.append("dealerGst", dealerGst);
    formData.append("dealerId", dealerId);

    try {
      setUploadingPdf(true);

      const res = await api.post(`/admin/dealers/${dealerId}/upload-pdf`, formData);
      const data = res.data;

      toast.success("PDF uploaded!");

      // Prefer the URL returned by the backend; else pull latest from sheet.
      let newUrl = data?.pdf?.url || data?.pdfUrl || data?.url || "";

      if (!newUrl) {
        try {
          newUrl = await fetchPdfFromSheet(dealerGst);
        } catch (e) {
          console.error("Fetch latest pdf after upload failed:", e);
        }
      }

      if (newUrl) {
        setDealer((prev) => ({ ...(prev || {}), pdf: { url: newUrl } }));
        setHasPdf(true);
      } else {
        setHasPdf(true);
      }

      setPdfFile(null);
      setShowPdfModal(false);
    } catch (err) {
      console.error("PDF upload error:", err);
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        (status ? `Upload failed (HTTP ${status})` : "Upload error");
      toast.error(msg);
    } finally {
      setUploadingPdf(false);
    }
  };

  const openPdf = () => {
    const url = dealer?.pdf?.url;
    if (!url) return toast.error("No PDF available");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-full text-2xl md:text-3xl">
              🎁
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Dealer Rewards</h1>
              {dealer && (
                <p className="text-sm md:text-base text-purple-100 opacity-90 mt-0.5">
                  {dealer.name || "Dealer"}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:text-purple-100 text-2xl md:text-3xl p-2 rounded-full hover:bg-white/10 transition"
              title="Go back"
            >
              <FaArrowLeft />
            </button>

            <button
              onClick={saveRewards}
              disabled={saving || !rows.some((r) => r.new)}
              className={`px-5 md:px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition ${
                saving || !rows.some((r) => r.new)
                  ? "bg-white text-black cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white shadow-sm"
              }`}
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">Saving...</span>
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 md:px-6 md:py-6">
        <div className="hidden md:block mb-6 p-5 bg-white border border-purple-200 rounded-2xl shadow-sm">
          <h3 className="text-xl font-bold text-purple-800 mb-4">
            Add New Reward Range
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <input
              type="number"
              placeholder="From KG"
              value={fromKg}
              onChange={(e) => setFromKg(e.target.value)}
              className="border rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <input
              type="number"
              placeholder="To KG"
              value={toKg}
              onChange={(e) => setToKg(e.target.value)}
              className="border rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <div className="relative lg:col-span-2">
              <input
                type="text"
                placeholder="Rewards (COOKER / BLENDER / FAN)"
                value={rewardsInput}
                onChange={(e) => {
                  setRewardsInput(e.target.value.toUpperCase());
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                className="border rounded-xl p-3 w-full focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-20 bg-white border rounded-xl mt-1 w-full shadow-lg max-h-48 overflow-y-auto">
                  {suggestions.map((item, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        setRewardsInput((prev) =>
                          formatRewards(prev + " / " + item),
                        );
                        setShowSuggestions(false);
                      }}
                      className="px-4 py-2.5 cursor-pointer hover:bg-purple-50 border-b last:border-b-0 text-sm"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={addRow}
              className="lg:col-start-4 bg-yellow-600 text-white rounded-xl font-bold hover:bg-yellow-700 px-6 py-3 transition"
            >
              Add Row
            </button>
          </div>
        </div>

        {rows.length > 0 ? (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="p-3 md:p-4 text-left font-semibold whitespace-nowrap">
                        Target (KG)
                      </th>
                      <th className="p-3 md:p-4 text-left font-semibold">
                        Rewards
                      </th>
                      <th className="p-3 md:p-4 text-center font-semibold w-16 md:w-20">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index} className="border-t hover:bg-gray-50">
                        <td className="p-3 md:p-4 font-medium whitespace-nowrap">
                          {row.range}
                        </td>
                        <td className="p-3 md:p-4">{row.rewards}</td>
                        <td className="p-3 md:p-4 text-center">
                          <button
                            onClick={() => removeRow(index)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 md:p-2 rounded-full transition"
                            title="Remove"
                          >
                            <FaTrash className="text-base md:text-lg" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Desktop PDF upload section */}
            <div className="hidden md:block p-5 bg-white border border-blue-200 rounded-2xl shadow-sm">
              <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                <span className="text-xl">📄</span> Dealer PDF Document
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 flex flex-wrap items-center gap-3">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                    className="block w-full max-w-xs text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {pdfFile && (
                    <button
                      onClick={uploadPdf}
                      disabled={uploadingPdf}
                      className={`px-5 py-2 rounded-xl text-white font-medium flex items-center gap-2 whitespace-nowrap ${
                        uploadingPdf
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    >
                      <FaUpload />
                      {uploadingPdf ? "Uploading..." : "Upload"}
                    </button>
                  )}
                </div>

                {/* ✅ SHOW if dealer.pdf.url exists */}
                {!!dealer?.pdf?.url && (
                  <button
                    onClick={openPdf}
                    className="flex items-center gap-2 text-sm px-5 py-2 rounded-full bg-green-100 text-green-800 hover:bg-green-200 whitespace-nowrap"
                  >
                    📄 View PDF
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Upload latest reward/incentive PDF. Recommended size: under 5MB.
              </p>
            </div>

            {/* Mobile PDF summary */}
            <div className="md:hidden bg-white rounded-2xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaFileUpload className="text-blue-600" /> Dealer PDF
                </h3>

                {!!dealer?.pdf?.url && (
                  <button
                    onClick={openPdf}
                    className="text-sm bg-green-100 text-green-700 px-3 py-1.5 rounded-full hover:bg-green-200"
                  >
                    View PDF
                  </button>
                )}
              </div>

              <p className="text-sm text-gray-500">
                {dealer?.pdf?.url
                  ? "PDF is uploaded — you can replace it"
                  : "No PDF uploaded yet"}
              </p>
              <p className="text-xs text-gray-400 mt-2">Recommended: under 5MB</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 md:py-16 text-gray-600 bg-white rounded-2xl border shadow-sm">
            No reward ranges added yet.
            <br className="md:hidden" />
            <span className="md:hidden">Tap the + button below.</span>
            <span className="hidden md:inline">
              Use the form above to add ranges.
            </span>
          </div>
        )}
      </div>

      {/* Mobile floating action buttons */}
      <div className="md:hidden fixed bottom-6 right-5 flex flex-col gap-4 z-20">
        <button
          onClick={() => setShowPdfModal(true)}
          className="bg-blue-600 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 transition active:scale-95"
          title="Upload PDF"
        >
          <FaUpload size={22} />
        </button>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-yellow-600 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-yellow-700 transition active:scale-95"
          title="Add Reward Range"
        >
          <FaPlus size={24} />
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Delete Reward Range?
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this reward range? This action
              cannot be undone.
            </p>

            <div className="flex gap-4 justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteIndex(null);
                }}
                className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  setShowDeleteConfirm(false);

                  const row = rows[deleteIndex];

                  try {
                    const res = await api.delete(
                      `/admin/dealers/${dealerId}/reward`,
                      {
                        data: { fromKg: row.fromKg, toKg: row.toKg },
                      },
                    );

                    if (res.data?.success) {
                      toast.success("Reward range deleted", {
                        icon: "✅",
                        duration: 2500,
                      });
                      setRows((prev) =>
                        prev.filter((_, i) => i !== deleteIndex),
                      );
                    } else {
                      toast.error(res.data?.message || "Delete failed");
                    }
                  } catch (err) {
                    toast.error("Failed to delete");
                    console.error(err);
                  }

                  setDeleteIndex(null);
                }}
                className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Add Reward Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end z-50 md:hidden">
          <div className="bg-white w-full rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-purple-800">
                Add Reward Range
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500"
              >
                <FaTimes size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="number"
                placeholder="From KG"
                value={fromKg}
                onChange={(e) => setFromKg(e.target.value)}
                className="border rounded-xl p-3.5 w-full focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="number"
                placeholder="To KG"
                value={toKg}
                onChange={(e) => setToKg(e.target.value)}
                className="border rounded-xl p-3.5 w-full focus:ring-2 focus:ring-purple-500"
              />
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rewards (COOKER / BLENDER / FAN)"
                  value={rewardsInput}
                  onChange={(e) => {
                    setRewardsInput(e.target.value.toUpperCase());
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  className="border rounded-xl p-3.5 w-full focus:ring-2 focus:ring-purple-500"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-10 bg-white border rounded-xl mt-1 w-full shadow-lg max-h-48 overflow-y-auto">
                    {suggestions.map((item, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setRewardsInput((prev) =>
                            formatRewards(prev + " / " + item),
                          );
                          setShowSuggestions(false);
                        }}
                        className="px-4 py-3 cursor-pointer hover:bg-purple-50 border-b last:border-b-0"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-3 bg-gray-200 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={addRow}
                  className="flex-1 py-3 bg-yellow-600 text-white rounded-xl font-bold hover:bg-yellow-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile PDF Upload Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end z-50 md:hidden">
          <div className="bg-white w-full rounded-t-3xl p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-blue-800">Upload PDF</h2>
              <button
                onClick={() => setShowPdfModal(false)}
                className="text-gray-500"
              >
                <FaTimes size={24} />
              </button>
            </div>
            <div className="space-y-5">
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-5 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {pdfFile && (
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-xl break-all">
                  Selected: {pdfFile.name}
                </div>
              )}
              <div className="flex gap-3 pt-3">
                <button
                  onClick={() => setShowPdfModal(false)}
                  className="flex-1 py-3 bg-gray-200 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadPdf}
                  disabled={uploadingPdf || !pdfFile}
                  className={`flex-1 py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 ${
                    uploadingPdf || !pdfFile
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  <FaUpload />
                  {uploadingPdf ? "Uploading..." : "Upload PDF"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
